<template>
  <!-- 图片轮播：左右滑动切换（参考小米官网）；
       历史要求记录：标题覆盖层位置上移（top 百分比），并通过变量统一放大字体（--overlay-scale）。 -->
  <!-- 说明：保留自动切换（定时器）+ 手动切换（箭头/圆点 + 原生拖拽/触控滑动） -->
  <div class="carousel-container" :style="{ height: height + 'px' }" @mouseenter="stopAutoplay" @mouseleave="startAutoplay">
    <!-- Slides（可拖拽/可触控滑动） -->
    <div class="scroll-container" ref="scrollContainer" @scroll="handleScroll">
      <div
        v-for="(item, index) in slides"
        :key="index"
        class="slide-item"
        :class="{ active: currentIndex === index }"
      >
        <img :src="item.img" alt="" class="slide-blur-bg" draggable="false" aria-hidden="true" />
        <img :src="item.img" :alt="item.titleCn" class="slide-bg" draggable="false" />
        
              </div>
    </div>

    <!-- Navigation Buttons -->
    <button class="nav-btn prev" @click="prevSlide" aria-label="Previous Slide">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18L9 12L15 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>
    <button class="nav-btn next" @click="nextSlide" aria-label="Next Slide">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 18L15 12L9 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>

    <!-- Pagination -->
    <div class="pagination">
      <span
        v-for="(_, index) in slides"
        :key="'dot-' + index"
        class="dot"
        :class="{ active: currentIndex === index }"
        @click="goToSlide(index)"
      ></span>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';

// 导入图片资源
import img1 from '@/assets/carousel_img/1.webp';
import img2 from '@/assets/carousel_img/2.webp';
import img3 from '@/assets/carousel_img/3.webp';
import img4 from '@/assets/carousel_img/4.webp';

@Options({
  props: {
    height: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentIndex: 0,
      timer: null as number | null,
      observer: null as IntersectionObserver | null,
      scrollRaf: null as number | null,
      slides: [
        {
          img: img1,
          titleCn: '模玩降世',
          titleEn: '',
          showLearnMore: true,
          customBuy: false,
        },
        {
          img: img2,
          titleCn: '硬核考证',
          titleEn: '',
          showLearnMore: true,
          customBuy: false,
        },
        {
          img: img3,
          titleCn: '国产国货',
          titleEn: '',
          showLearnMore: false, // 只有这个 slide 不显示“了解更多”链接（根据原代码逻辑）
          customBuy: true,
          productName: '幻影2000',
        },
        {
          img: img4,
          titleCn: '与时俱进',
          titleEn: '',
          showLearnMore: true,
          customBuy: false,
        },
      ]
    };
  },
  mounted() {
    // 立即启动自动播放，不等待 IntersectionObserver 的首次回调
    this.startAutoplay();
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    this.stopAutoplay();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }
  },
  methods: {
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 进入视口，确保启动（如果之前被暂停了）
            this.startAutoplay();
          } else {
            // 离开视口，停止轮播
            this.stopAutoplay();
          }
        });
      }, {
        threshold: 0.1
      });

      const container = this.$refs.scrollContainer as Element;
      if (container) {
        this.observer.observe(container);
      }
    },
    startAutoplay() {
      if (this.timer) return;
      this.timer = window.setInterval(this.nextSlide, 5000);
    },
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    nextSlide() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.scrollToIndex(nextIndex);
    },
    prevSlide() {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.scrollToIndex(prevIndex);
    },
    goToSlide(index: number) {
      this.scrollToIndex(index);
    },
    scrollToIndex(index: number) {
      const container = this.$refs.scrollContainer as HTMLElement;
      if (!container) return;
      const width = container.clientWidth;
      container.scrollTo({ left: index * width, behavior: 'smooth' });
    },
    handleScroll() {
      const container = this.$refs.scrollContainer as HTMLElement;
      if (!container) return;
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        const width = container.clientWidth || 1;
        const index = Math.round(container.scrollLeft / width);
        if (index >= 0 && index < this.slides.length) {
          this.currentIndex = index;
        }
      });
    },
    handleBuyNow(productName: string) {
      this.$router.push({
        path: '/wechatPay',
        query: { productName: productName }
      });
    },
  },
})
export default class CarouselImg extends Vue {}
</script>

<style scoped lang="scss">
.carousel-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
  --overlay-scale: 1.2;
}

/* 旧版：Fade 渐变切换（已按需求注释保留）
 * - 通过 opacity + absolute 叠放实现淡入淡出
 */
/*
 .slide-item {
   position: absolute;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   opacity: 0;
   z-index: 0;
   transition: opacity 600ms ease-in-out;
   pointer-events: none;
 }
 .slide-item.active {
   opacity: 1;
   z-index: 1;
   pointer-events: auto;
   transition: opacity 600ms ease-in-out;
 }
 .slide-item:not(.active) {
   transition-delay: 600ms;
 }
*/

/* 新版：左右滑动切换（原生滚动 + Scroll Snap） */
.scroll-container {
  width: 100%;
  height: 100%;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.scroll-container::-webkit-scrollbar { display: none; }

.slide-item {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  position: relative;
  scroll-snap-align: start;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slide-bg {
  position: relative;
  z-index: 1;
  width: auto;
  height: 100%;
  object-fit: contain;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}

.slide-blur-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(20px);
  transform: scale(1.12);
  user-select: none;
  -webkit-user-drag: none;
}

/* Navigation Buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  &:hover {
    /* 因优化样式（2026-03），去除了半透明圆背景，改为改变箭头颜色 */
    /* background-color: rgba(0,0,0,0.1); */
    /* border-radius: 50%; */
  }
  
  /* 鼠标悬停时，SVG 路径颜色变为指定绿色 RGB(87,113,59) */
  &:hover path {
    stroke: #FF6900;
  }
}

.carousel-container:hover .nav-btn {
  opacity: 1;
}

.prev { left: 20px; }
.next { right: 20px; }

/* Pagination */
.pagination {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 10;
  width: fit-content;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 2px;
}

.dot {
  width: 40px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.dot.active {
  background-color: #FF6900;
}

</style>
